import type { Language, ListItem } from '@indutrust/shared-model';
import { translate } from '@jsverse/transloco';

/**
 * Build grouped select options from an array of {@link Language} objects.
 *
 * Manually translated languages are listed at the top level, while automatically
 * translated ones are grouped under a parent option whose label is resolved via Transloco.
 *
 * @param languages Array of {@link Language} items to convert to select options.
 * @param currentLanguage The currently selected {@link Language}, used to scope the translation key.
 * @returns A list of {@link ListItem} where auto-translated languages are nested as children.
 * @example
 * const options = getLanguageOptions(languages, currentLanguage);
 */
export function getLanguageOptions(
  languages: Language[],
  currentLanguage: Language
): ListItem<Language['code']>[] {
  const manuallyTranslatedLanguages = languages.filter(
    (language) => !language.isAutoTranslated
  );
  const automaticallyTranslatedLanguages = languages.filter(
    (language) => language.isAutoTranslated
  );
  return [
    ...manuallyTranslatedLanguages.map((language) => ({
      value: language.code,
      label: language.displayName,
    })),
    {
      label: translate(
        'languageSelector.automaticTranslationsLabel',
        {},
        `i18n/${currentLanguage.code}`
      ),
      value: '',
      children: automaticallyTranslatedLanguages.map((language) => ({
        value: language.code,
        label: language.displayName,
      })),
    },
  ];
}

/**
 * Build select options for manually translated languages only.
 *
 * @param languages Array of {@link Language} to include.
 * @returns A flat list of {@link ListItem} for languages where `isAutoTranslated` is false.
 * @example
 * const options = getEffectiveLanguageOptions(languages);
 */
export function getEffectiveLanguageOptions(
  languages: Language[]
): ListItem<Language['code']>[] {
  return languages
    .filter((language) => !language.isAutoTranslated)
    .map((language) => ({
      value: language.code,
      label: language.displayName,
    }));
}

/**
 * Return the primary locale (first two letters) from the browser's navigator language.
 *
 * Falls back to the full `navigator.language` if its length is 2 or less.
 *
 * @returns The two-letter locale code (e.g., "en"), or an empty string if not available.
 * @example
 * const locale = getSerializedNavigatorLanguage();
 */
export function getSerializedNavigatorLanguage(): string {
  return navigator.language && navigator.language.length > 2
    ? navigator.language.slice(0, 2)
    : navigator.language;
}
